import { useCallback, useState } from 'react';

import { useIntl } from 'react-intl-next';
import { usePathParam } from 'react-resource-router';

import { useDefaultCatch } from '@atlassian/eoc-catch';
import { assert, AssertionError, errorToastProps, successToastProps } from '@atlassian/eoc-common';
import { useFlags } from '@atlassian/eoc-global-context';
import { useResource } from '@atlassian/eoc-rrr-helpers';

import {
	type ChangeOrderRoutingRuleParams,
	type CreateRoutingRuleParams,
	type Entity,
	type RoutingRulesResponse,
	type UpdateRoutingRuleParams,
} from '../../common/types';
import { routingRulesResource } from '../resources';

import {
	changeOrderRoutingRuleEndpoint,
	completeTaskEndpoint,
	createRoutingRuleEndpoint,
	deleteRoutingRuleEndpoint,
	saveViewEndpoint,
	updateRoutingRuleEndpoint,
} from './endpoints';
import messages from './messages';

export const useRoutingRuleService = () => {
	const { data, update } = useResource(routingRulesResource);
	const [teamId] = usePathParam('teamId');
	const defaultCatch = useDefaultCatch();
	const [isCreating, setIsCreating] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [isChangingOrder, setIsChangingOrder] = useState(false);
	const flagApi = useFlags();
	const { formatMessage } = useIntl();

	assert(
		!!data && !!teamId,
		new AssertionError(
			'useRoutingRuleService',
			'We should not render useRoutingRuleService before teamId is fetched',
		),
	);

	const createRoutingRule = useCallback(
		(routingRule: CreateRoutingRuleParams) => {
			setIsCreating(true);

			return createRoutingRuleEndpoint(teamId, routingRule)
				.then((res) => {
					flagApi.showFlag({
						...successToastProps(),
						title: formatMessage(messages.routingRuleCreated, {
							name: routingRule.name || false,
						}),
					});

					update(() => res);

					const lastIndex = res.routingRules.entities.length - 2;
					const addedEntity = res.routingRules.entities[lastIndex];

					return addedEntity;
				})
				.catch(
					defaultCatch({
						id: 'useRoutingRuleService-createRoutingRule',
						httpErrorHandler: (err) => {
							flagApi.showFlag({
								...errorToastProps(),
								description: err.message,
								title: formatMessage(messages.createError),
							});

							throw err;
						},
					}),
				)
				.finally(() => setIsCreating(false));
		},
		[defaultCatch, flagApi, formatMessage, teamId, update],
	);

	const updateRoutingRule = useCallback(
		(routingRule: UpdateRoutingRuleParams) => {
			setIsUpdating(true);

			return updateRoutingRuleEndpoint(teamId, routingRule)
				.then((res) => {
					flagApi.showFlag({
						...successToastProps(),
						title: formatMessage(
							routingRule.isDefault
								? messages.defaultRoutingRuleUpdated
								: messages.routingRuleUpdated,
							{
								name: routingRule.name || false,
							},
						),
					});
					update(() => res);
				})
				.catch(
					defaultCatch({
						id: 'useRoutingRuleService-updateRoutingRule',
						httpErrorHandler: (err) => {
							flagApi.showFlag({
								...errorToastProps(),
								description: err.message,
								title: formatMessage(messages.updateError),
							});

							throw err;
						},
					}),
				)
				.finally(() => setIsUpdating(false));
		},
		[defaultCatch, flagApi, formatMessage, teamId, update],
	);

	const deleteRoutingRule = useCallback(
		(routingRuleId: Entity['id'], name: Entity['name']) => {
			setIsDeleting(true);

			return deleteRoutingRuleEndpoint(teamId, routingRuleId)
				.then((res) => {
					flagApi.showFlag({
						...successToastProps(),
						title: formatMessage(messages.routingRuleDeleted, {
							name: name || false,
						}),
					});

					const entities = data.routingRules.entities.filter(
						(entity) => entity.id !== routingRuleId,
					);

					update(() => ({
						...data,
						routingRules: {
							...data.routingRules,
							entities,
						},
					}));
				})
				.catch(
					defaultCatch({
						id: 'useRoutingRuleService-deleteRoutingRule',
						httpErrorHandler: (err) => {
							flagApi.showFlag({
								...errorToastProps(),
								description: err.message,
								title: formatMessage(messages.deleteError),
								actions: [
									{
										content: formatMessage(messages.refresh),
										onClick: () => window.location.reload(),
									},
								],
							});

							throw err;
						},
					}),
				)
				.finally(() => setIsDeleting(false));
		},
		[data, defaultCatch, flagApi, formatMessage, teamId, update],
	);

	const changeOrderRoutingRule = useCallback(
		(payload: ChangeOrderRoutingRuleParams, updatedValue: Entity[]) => {
			setIsChangingOrder(true);

			update(() => {
				return {
					...data,
					routingRules: {
						...data.routingRules,
						entities: updatedValue,
					},
				};
			});

			return changeOrderRoutingRuleEndpoint(teamId, payload)
				.catch(
					defaultCatch({
						id: 'useRoutingRuleService-changeOrderRoutingRule',
						httpErrorHandler: (err) => {
							flagApi.showFlag({
								...errorToastProps(),
								description: err.message,
								title: formatMessage(messages.reorderError),
								actions: [
									{
										content: formatMessage(messages.refresh),
										onClick: () => window.location.reload(),
									},
								],
							});

							throw err;
						},
					}),
				)
				.finally(() => setIsChangingOrder(false));
		},
		[data, defaultCatch, flagApi, formatMessage, teamId, update],
	);

	const saveView = useCallback(() => {
		const collapsed = data.routingRules.collapsed;

		const newData: RoutingRulesResponse = {
			...data,
			routingRules: {
				...data.routingRules,
				collapsed: !collapsed,
			},
		};

		update(() => newData);

		const view = {
			collapsed: !collapsed,
		};

		return saveViewEndpoint(teamId, view).catch(
			defaultCatch({
				id: 'useRoutingRuleService-saveView',
				httpErrorHandler: (err) => {
					flagApi.showFlag({
						...errorToastProps(),
						description: err.message,
						title: formatMessage(messages.saveViewError),
						actions: [
							{
								content: formatMessage(messages.refresh),
								onClick: () => window.location.reload(),
							},
						],
					});

					throw err;
				},
			}),
		);
	}, [data, defaultCatch, flagApi, formatMessage, teamId, update]);

	const completeTask = useCallback(() => {
		return completeTaskEndpoint(teamId);
	}, [teamId]);

	return {
		isCreating,
		isUpdating,
		isDeleting,
		isChangingOrder,
		createRoutingRule,
		updateRoutingRule,
		deleteRoutingRule,
		changeOrderRoutingRule,
		saveView,
		completeTask,
	};
};
